import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    IrrigationScheduleConfig,
    IrrigationScheduleSaveRequest,
} from '@/lib/api/irrigation-devices-api'
import { irrigationDevicesApi } from '@/lib/api/irrigation-devices-api'
import { irrigationDevicesKeys } from '@/hooks/use-irrigation-devices-query'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

/** Phiên đang chạy đổi trạng thái theo giờ: làm mới như danh sách bộ tưới. */
export const IRRIGATION_RUNS_REFRESH_MS = 30_000

export const irrigationScheduleKeys = {
    schedule: (deviceId: string) => ['irrigation-schedule', deviceId] as const,
    preview: (config: IrrigationScheduleConfig) =>
        ['irrigation-schedule-preview', JSON.stringify(config)] as const,
}

export function useIrrigationSchedule(deviceId: string, enabled = true) {
    return useQuery({
        queryKey: irrigationScheduleKeys.schedule(deviceId),
        queryFn: async () => {
            const res = await irrigationDevicesApi.getSchedule(deviceId)
            return res.data ?? null
        },
        enabled,
        // Lỗi thì báo ngay (form không hiện khi chưa đọc được lịch thật);
        // người dùng bấm "Thử lại" nếu muốn.
        retry: false,
    })
}

/** Xem trước do backend tính (quy tắc lịch chỉ nằm một chỗ). config null =
 *  form chưa hợp lệ, không gọi. */
export function useIrrigationSchedulePreview(
    config: IrrigationScheduleConfig | null,
) {
    return useQuery({
        queryKey: config
            ? irrigationScheduleKeys.preview(config)
            : ['irrigation-schedule-preview', null],
        queryFn: async () => {
            if (!config) return { sessions: [], warnings: [] }
            const res = await irrigationDevicesApi.previewSchedule(config)
            return res.data ?? { sessions: [], warnings: [] }
        },
        enabled: config !== null,
        retry: false,
        placeholderData: keepPreviousData,
    })
}

export function useSaveIrrigationSchedule(
    deviceId: string,
    deviceName: string,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (body: IrrigationScheduleSaveRequest) => {
            const res = await irrigationDevicesApi.saveSchedule(deviceId, body)
            return res.data
        },
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: irrigationScheduleKeys.schedule(deviceId),
            })
            void qc.invalidateQueries({
                queryKey: irrigationDevicesKeys.runs(deviceId),
            })
            toast.success(m.irrigation_schedule_saved({ name: deviceName }))
        },
        onError: (err) => {
            const p = formatApiError(err, m.irrigation_schedule_save_error())
            toast.error(p.title, { description: p.description })
        },
    })
}

export function useIrrigationRuns(deviceId: string, enabled = true) {
    return useQuery({
        queryKey: irrigationDevicesKeys.runs(deviceId),
        queryFn: async () => {
            const res = await irrigationDevicesApi.listRuns(deviceId)
            return res.data ?? []
        },
        enabled,
        refetchInterval: IRRIGATION_RUNS_REFRESH_MS,
    })
}

export function useCancelIrrigationRun(deviceId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (runId: string) => {
            await irrigationDevicesApi.cancelRun(runId)
        },
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: irrigationDevicesKeys.runs(deviceId),
            })
            toast.success(m.irrigation_run_cancelled())
        },
        onError: (err) => {
            const p = formatApiError(err, m.irrigation_run_cancel_error())
            toast.error(p.title, { description: p.description })
        },
    })
}
